import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, PhoneCall, Mail, MessageSquare, ChevronRight, Send, ShieldAlert,
  CheckCircle2, User, Wrench,
} from 'lucide-react';
import { biz } from '../../lib/api';
import { StatusBadge, PriorityBadge } from './BusinessLayout';

const STATUS_FLOW = [
  'NEW', 'ACKNOWLEDGED', 'ASSIGNED', 'UNDER_INVESTIGATION',
  'ACTION_REQUIRED', 'CUSTOMER_CONFIRMATION', 'RESOLVED', 'CLOSED',
];
const ALT_STATUS = ['ON_HOLD', 'ESCALATED', 'REOPENED', 'CANCELLED'];

export default function ComplaintDetail() {
  const { no } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showComm, setShowComm] = useState(false);
  const [showStatus, setShowStatus] = useState(false);

  const load = useCallback(async () => {
    const r = await biz.getComplaint(no);
    setData(r);
  }, [no]);

  useEffect(() => {
    load();
    biz.listTemplates().then(setTemplates);
  }, [load]);

  if (!data) return <div className="p-6 text-ink/60" data-testid="biz-complaint-loading">Loading complaint…</div>;
  const c = data.complaint;

  return (
    <div className="grid gap-3" data-testid="biz-complaint-detail">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to="/business/complaints" className="pill !bg-white text-xs"><ArrowLeft className="w-3 h-3"/>Back</Link>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-black text-[#0D47A1]" data-testid="biz-complaint-no">{c.complaint_number}</div>
          <h1 className="font-heading font-black text-xl md:text-2xl">{c.customer_name}{c.product ? ` — ${c.product}` : ''}</h1>
        </div>
        <div className="ml-auto flex items-center gap-1 flex-wrap">
          <StatusBadge value={c.status}/>
          <PriorityBadge value={c.priority}/>
          {c.sla_breached && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FFEBEE] text-[#B71C1C] font-bold" data-testid="biz-complaint-sla-breached"><ShieldAlert className="w-3 h-3 inline mr-1"/>SLA breached</span>}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <div className="card-soft p-3 text-sm">
          <div className="text-[10px] uppercase tracking-widest text-ink/50">Customer</div>
          <div className="mt-1 font-semibold">{c.customer_name}</div>
          <div className="text-xs">{c.mobile}</div>
          <div className="text-xs text-ink/60">{c.email}</div>
          <div className="text-xs text-ink/60">{c.address}</div>
          <a href={`tel:${c.mobile}`} className="pill !bg-white text-xs mt-2 mr-1" data-testid="biz-comm-call"><PhoneCall className="w-3 h-3"/>Call</a>
          <a href={`https://wa.me/${(c.mobile || '').replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="pill !bg-white text-xs mt-2 mr-1" data-testid="biz-comm-wa"><MessageSquare className="w-3 h-3"/>WhatsApp</a>
          {c.email && <a href={`mailto:${c.email}`} className="pill !bg-white text-xs mt-2" data-testid="biz-comm-mail"><Mail className="w-3 h-3"/>Email</a>}
        </div>
        <div className="card-soft p-3 text-sm">
          <div className="text-[10px] uppercase tracking-widest text-ink/50">Product & warranty</div>
          <div>{c.product} {c.product_model && `· ${c.product_model}`}</div>
          <div className="text-xs">S/N: {c.serial_number || '—'} · Invoice: {c.invoice_number || '—'}</div>
          <div className="text-xs text-ink/60">Warranty: {c.warranty_status}</div>
          <div className="text-xs text-ink/60">Category: {c.category}{c.sub_category ? ` / ${c.sub_category}` : ''}</div>
          <div className="text-xs text-ink/60">Source: {c.source}</div>
        </div>
        <div className="card-soft p-3 text-sm">
          <div className="text-[10px] uppercase tracking-widest text-ink/50">SLA</div>
          <div className="text-xs">Response due: {new Date(c.sla_response_due).toLocaleString()}</div>
          <div className="text-xs">Resolution due: {new Date(c.sla_resolution_due).toLocaleString()}</div>
          <div className="text-xs">Elapsed: {c.elapsed_hours}h · Remaining: {c.remaining_hours}h</div>
          <div className="text-xs text-ink/60 mt-2"><User className="w-3 h-3 inline"/> Assigned: {c.assigned_to_name || '—'}</div>
          <div className="text-xs text-ink/60"><Wrench className="w-3 h-3 inline"/> Registered by: {c.registered_by_name}</div>
        </div>
      </div>

      <section className="card-soft p-3">
        <div className="text-[10px] uppercase tracking-widest text-ink/50">Description</div>
        <div className="text-sm mt-1">{c.description}</div>
        {(c.attachments || []).filter((a) => a.url).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2" data-testid="biz-complaint-photos">
            {c.attachments.filter((a) => a.url).map((a, i) => (
              <a key={a.url} href={a.url} target="_blank" rel="noreferrer" data-testid={`biz-complaint-photo-${i}`}>
                <img src={a.url} alt={`Complaint photo ${i + 1}`} className="w-24 h-24 rounded-lg object-cover border border-border"/>
              </a>
            ))}
          </div>
        )}
      </section>

      <section className="flex flex-wrap gap-2">
        <button className="btn-primary" onClick={() => setShowStatus(true)} data-testid="biz-change-status-btn"><ChevronRight className="w-4 h-4"/>Change status</button>
        <button className="pill !bg-white" onClick={() => setShowTimeline(true)} data-testid="biz-add-timeline-btn">+ Timeline entry</button>
        <button className="pill !bg-white" onClick={() => setShowComm(true)} data-testid="biz-add-comm-btn">+ Communication</button>
      </section>

      <section className="card-soft p-3" data-testid="biz-complaint-timeline">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-ink/60">Timeline</div>
        <div className="mt-2 border-l-2 border-[#E67E22]/40 pl-3 grid gap-3">
          {data.timeline.map((t) => (
            <div key={t.id} className="relative">
              <div className="absolute -left-[13px] w-2.5 h-2.5 rounded-full bg-[#E67E22] mt-1"></div>
              <div className="text-xs text-ink/60">{new Date(t.at).toLocaleString()} · {t.actor_name}</div>
              <div className="text-sm font-semibold">{t.action}</div>
              {t.from_status && t.to_status && t.from_status !== t.to_status &&
                <div className="text-[11px] text-ink/60">{t.from_status} → {t.to_status}</div>}
              {t.closure_reason && <div className="text-[11px] text-ink/70 italic">Closure: {t.closure_reason}</div>}
              {t.observation && <div className="text-sm text-ink/70 mt-1">{t.observation}</div>}
              {t.diagnosis && <Kv k="Diagnosis" v={t.diagnosis}/>}
              {t.root_cause && <Kv k="Root cause" v={t.root_cause}/>}
              {t.corrective_action && <Kv k="Corrective" v={t.corrective_action}/>}
              {t.preventive_action && <Kv k="Preventive" v={t.preventive_action}/>}
              {t.spare_parts?.length > 0 && <div className="text-[11px] text-ink/60 mt-1">Parts: {t.spare_parts.map((p) => `${p.name} × ${p.qty}`).join(', ')}</div>}
              {t.customer_communication && <Kv k="Customer msg" v={t.customer_communication}/>}
              {t.customer_response && <Kv k="Customer resp" v={t.customer_response}/>}
              {t.remarks && <div className="text-[11px] text-ink/60">{t.remarks}</div>}
            </div>
          ))}
        </div>
      </section>

      <section className="card-soft p-3" data-testid="biz-complaint-comms">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-ink/60">Customer Communications</div>
        <div className="mt-2 grid gap-2">
          {data.comms.length ? data.comms.map((m) => (
            <div key={m.id} className="border border-border rounded-lg p-2 text-sm">
              <div className="text-[10px] uppercase tracking-widest text-ink/50">{new Date(m.at).toLocaleString()} · {m.comm_type} · {m.actor_name}</div>
              <div className="mt-1 whitespace-pre-wrap">{m.message}</div>
              {m.customer_response && <div className="mt-1 text-xs text-ink/70">↩ Customer: {m.customer_response}</div>}
            </div>
          )) : <div className="text-xs text-ink/50">No communications logged yet.</div>}
        </div>
      </section>

      {showStatus && <StatusModal complaint={c} onClose={() => setShowStatus(false)} onDone={() => { setShowStatus(false); load(); }}/>}
      {showTimeline && <TimelineModal cno={c.complaint_number} onClose={() => setShowTimeline(false)} onDone={() => { setShowTimeline(false); load(); }}/>}
      {showComm && <CommModal cno={c.complaint_number} customer={c} templates={templates}
        onClose={() => setShowComm(false)} onDone={() => { setShowComm(false); load(); }}/>}
    </div>
  );
}

function Kv({ k, v }) {
  return <div className="text-[11px] text-ink/70 mt-1"><b className="uppercase tracking-widest text-[9px] text-ink/50">{k}:</b> {v}</div>;
}

function StatusModal({ complaint, onClose, onDone }) {
  const [target, setTarget] = useState(nextInFlow(complaint.status));
  const [remarks, setRemarks] = useState('');
  const [reason, setReason] = useState('');
  const [assignee, setAssignee] = useState(complaint.assigned_to_name || '');
  const submit = async () => {
    if (target === 'CLOSED' && !reason && !complaint.closure_reason) {
      toast.error('Closure reason is mandatory'); return;
    }
    try {
      await biz.changeStatus(complaint.complaint_number, {
        new_status: target, remarks, closure_reason: reason || undefined,
        assigned_to_name: assignee || undefined,
      });
      toast.success(`Status → ${target}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <Modal title="Change status" onClose={onClose} testid="biz-status-modal">
      <div className="grid gap-2 text-sm">
        <L label="Next status">
          <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="biz-status-select">
            {[...STATUS_FLOW, ...ALT_STATUS].map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
        </L>
        <L label="Assignee (optional)"><input value={assignee} onChange={(e) => setAssignee(e.target.value)} data-testid="biz-status-assignee"/></L>
        {target === 'CLOSED' && <L label="Closure reason*"><input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="biz-status-reason"/></L>}
        <L label="Remarks"><textarea rows="2" value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="biz-status-remarks"/></L>
        <button className="btn-primary mt-2" onClick={submit} data-testid="biz-status-submit">Save status</button>
      </div>
    </Modal>
  );
}

function nextInFlow(cur) {
  const i = STATUS_FLOW.indexOf(cur);
  return i >= 0 && i < STATUS_FLOW.length - 1 ? STATUS_FLOW[i + 1] : cur || 'ACKNOWLEDGED';
}

function TimelineModal({ cno, onClose, onDone }) {
  const [form, setForm] = useState({
    action: '', observation: '', diagnosis: '', root_cause: '',
    corrective_action: '', preventive_action: '',
    technician: '', spare_parts: [], customer_communication: '', customer_response: '',
    next_followup_date: '', remarks: '',
  });
  const [part, setPart] = useState({ name: '', qty: 1 });
  const set = (k, v) => setForm({ ...form, [k]: v });
  const submit = async () => {
    if (!form.action) { toast.error('Action is required'); return; }
    try {
      await biz.addTimeline(cno, form);
      toast.success('Timeline entry added');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const addPart = () => {
    if (!part.name) return;
    set('spare_parts', [...(form.spare_parts || []), { name: part.name, qty: Number(part.qty) }]);
    setPart({ name: '', qty: 1 });
  };
  return (
    <Modal title="Add investigation entry" onClose={onClose} testid="biz-timeline-modal">
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="col-span-full"><L label="Action*"><input value={form.action} onChange={(e) => set('action', e.target.value)} data-testid="biz-timeline-action"/></L></div>
        <L label="Observation"><textarea rows="2" value={form.observation} onChange={(e) => set('observation', e.target.value)}/></L>
        <L label="Diagnosis"><textarea rows="2" value={form.diagnosis} onChange={(e) => set('diagnosis', e.target.value)}/></L>
        <L label="Root cause"><textarea rows="2" value={form.root_cause} onChange={(e) => set('root_cause', e.target.value)}/></L>
        <L label="Corrective action"><textarea rows="2" value={form.corrective_action} onChange={(e) => set('corrective_action', e.target.value)}/></L>
        <L label="Preventive action"><textarea rows="2" value={form.preventive_action} onChange={(e) => set('preventive_action', e.target.value)}/></L>
        <L label="Technician"><input value={form.technician} onChange={(e) => set('technician', e.target.value)}/></L>
        <L label="Customer communication"><textarea rows="2" value={form.customer_communication} onChange={(e) => set('customer_communication', e.target.value)}/></L>
        <L label="Customer response"><textarea rows="2" value={form.customer_response} onChange={(e) => set('customer_response', e.target.value)}/></L>
        <L label="Next follow-up date"><input type="date" value={form.next_followup_date} onChange={(e) => set('next_followup_date', e.target.value)}/></L>
        <div className="col-span-full">
          <div className="text-[10px] uppercase tracking-widest text-ink/50">Spare parts used</div>
          <div className="flex gap-2 items-center mt-1">
            <input placeholder="Part name" value={part.name} onChange={(e) => setPart({ ...part, name: e.target.value })}
              className="rounded-lg border border-border px-2 py-1.5 flex-1"/>
            <input type="number" min="1" value={part.qty} onChange={(e) => setPart({ ...part, qty: e.target.value })}
              className="rounded-lg border border-border px-2 py-1.5 w-20"/>
            <button className="pill !bg-white text-xs" onClick={addPart}>+ Add</button>
          </div>
          <div className="mt-1 text-xs">
            {(form.spare_parts || []).map((p, i) => (
              <span key={i} className="pill !bg-[#FFF8EE] mr-1 mt-1">{p.name} × {p.qty}</span>
            ))}
          </div>
        </div>
        <div className="col-span-full"><L label="Remarks"><textarea rows="1" value={form.remarks} onChange={(e) => set('remarks', e.target.value)}/></L></div>
      </div>
      <button className="btn-primary mt-3" onClick={submit} data-testid="biz-timeline-submit"><Send className="w-4 h-4"/>Add entry</button>
    </Modal>
  );
}

function CommModal({ cno, customer, templates, onClose, onDone }) {
  const [type, setType] = useState('whatsapp');
  const [tpl, setTpl] = useState('');
  const [message, setMessage] = useState('');
  const [response, setResponse] = useState('');

  const pickTpl = (key) => {
    setTpl(key);
    const t = templates.find((x) => x.key === key);
    if (t) {
      setMessage(t.body
        .replace('{customer_name}', customer.customer_name || '')
        .replace('{complaint_number}', cno)
        .replace('{staff_name}', customer.assigned_to_name || 'our team'));
    }
  };
  const submit = async () => {
    if (!message) { toast.error('Message required'); return; }
    try {
      await biz.addComm(cno, { comm_type: type, template_key: tpl || undefined, message, customer_response: response || undefined });
      toast.success('Communication logged');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <Modal title="Log customer communication" onClose={onClose} testid="biz-comm-modal">
      <div className="grid gap-2 text-sm">
        <L label="Channel">
          <select value={type} onChange={(e) => setType(e.target.value)} data-testid="biz-comm-type">
            {['call', 'whatsapp', 'sms', 'email', 'walkin', 'other'].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </L>
        <L label="Template (optional)">
          <select value={tpl} onChange={(e) => pickTpl(e.target.value)} data-testid="biz-comm-template">
            <option value="">— none —</option>
            {templates.map((t) => <option key={t.key} value={t.key}>{t.title}</option>)}
          </select>
        </L>
        <L label="Message*"><textarea rows="4" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="biz-comm-message"/></L>
        <L label="Customer response (optional)"><textarea rows="2" value={response} onChange={(e) => setResponse(e.target.value)} data-testid="biz-comm-response"/></L>
        <button className="btn-primary mt-2" onClick={submit} data-testid="biz-comm-submit"><Send className="w-4 h-4"/>Save communication</button>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, testid, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-2" data-testid={testid} onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-2xl border border-border shadow-xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-heading font-bold">{title}</div>
          <button onClick={onClose}>✕</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function L({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-widest text-ink/50">{label}</span>
      <span className="[&>*]:w-full [&>*]:rounded-lg [&>*]:border [&>*]:border-border [&>*]:px-2 [&>*]:py-1.5 [&>*]:bg-white">{children}</span>
    </label>
  );
}
